import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { NavLink, useLocation } from "react-router-dom";
import { Home, BookOpen, User, Trophy, MessageCircle } from "lucide-react";
import { cachedGet } from "../api";

const StyledBottomNavBar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: 10px 0;
  z-index: 1000;
`;

const NavItem = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  text-decoration: none;
  font-size: 11px;
  transition: color 0.3s ease;
  position: relative;
  font-family: "Quicksand", sans-serif;
  font-weight: 600;

  &.active {
    color: #8747d4;
  }

  svg {
    margin-bottom: 4px;
    width: 24px;
    height: 24px;
  }
`;

const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -10px;
  background: #ff4757;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  min-width: 16px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const BottomNavBar = () => {
  const location = useLocation();
  const userEmail = localStorage.getItem("userEmail");
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    if (!userEmail) return undefined;

    let cancelled = false;

    // This bar is mounted on every screen, so the first read comes from the shared cache
    // (Salaam reads the same entry). Only the poll forces a real refresh.
    const fetchAnnouncements = (force) =>
      cachedGet("/api/announcements", {
        params: { email: userEmail },
        ttl: 30000,
        force,
      })
        .then((data) => {
          if (!cancelled) setAnnouncements(data || []);
        })
        .catch((error) => {
          console.error("Failed to fetch announcements:", error);
        });

    fetchAnnouncements(false);
    // Poll every 30 seconds for new announcements
    const interval = setInterval(() => fetchAnnouncements(true), 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userEmail]);

  const unreadCount = useMemo(() => {
    if (!userEmail) return 0;
    return announcements.filter(
      (a) => !(a.readBy || []).includes(userEmail)
    ).length;
  }, [announcements, userEmail]);

  const isSalaamActive =
    location.pathname === "/salaam" || location.pathname.startsWith("/salaam/");

  return (
    <StyledBottomNavBar>
      <NavItem to="/home" isActive={() => location.pathname === "/home"}>
        <Home strokeWidth={location.pathname === "/home" ? 2.5 : 1.5} />
        Home
      </NavItem>
      <NavItem to="/library" isActive={() => location.pathname === "/library"}>
        <BookOpen strokeWidth={location.pathname === "/library" ? 2.5 : 1.5} />
        Library
      </NavItem>
      <NavItem to="/salaam" isActive={() => isSalaamActive}>
        <IconWrapper>
          <MessageCircle strokeWidth={isSalaamActive ? 2.5 : 1.5} />
          {unreadCount > 0 && <Badge>{unreadCount}</Badge>}
        </IconWrapper>
        Salaam!
      </NavItem>
      <NavItem
        to="/leaderboard"
        isActive={() => location.pathname === "/leaderboard"}
      >
        <Trophy
          strokeWidth={location.pathname === "/leaderboard" ? 2.5 : 1.5}
        />
        Leaderboard
      </NavItem>
      <NavItem to="/profile" isActive={() => location.pathname === "/profile"}>
        <User strokeWidth={location.pathname === "/profile" ? 2.5 : 1.5} />
        Profile
      </NavItem>
    </StyledBottomNavBar>
  );
};

export default BottomNavBar;
